import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Dialog, FormSection, Grid, Select, Stack, t, TextField, toast } from '@kanbios/ui';
import type { maintenance } from '@kanbios/api-client';
import { api, ApiFailure, causeOf, unwrap } from '../../lib/api';
import { pendingLabel } from '../PendingButton';
import { changeRef } from './PullRequestNotice';

type Contact = maintenance.components['schemas']['Contact'];
const ROLES = ['technical', 'reports', 'dora-incidents', 'restitution'] as const;

/** « Modifier les contacts » (SPEC-005): a client's contacts, by pull request; Administrator only. */
export function ContactsDialog({ open, onClose, client, current }: { open: boolean; onClose: () => void; client: { name: string; title: string }; current: Contact[] }) {
  const queryClient = useQueryClient();
  const [contacts, setContacts] = useState<Contact[]>(current.length ? current : [{ role: 'technical', name: '', email: '' }]);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [sending, setSending] = useState(false);
  const fieldError = (i: number, field: string) => {
    const fields = (failure?.body.details?.fields ?? []) as { field: string }[];
    return fields.some(f => f.field === `contacts[${i}].${field}`) ? t(`contacts.errors.${field}`) : undefined;
  };
  const update = (i: number, patch: Partial<Contact>) => setContacts(list => list.map((c, j) => (j === i ? { ...c, ...patch } : c)));

  const submit = async () => {
    setSending(true);
    setFailure(null);
    try {
      const { pullRequest } = unwrap(await api.maintenance.PUT('/clients/{client}/contacts', {
        params: { path: { client: client.name } },
        body: { contacts: contacts.map(c => ({ ...c, mobile: c.role === 'restitution' ? c.mobile : undefined })) },
      }));
      toast({ title: t('contacts.opened', { ref: changeRef(pullRequest) }), tone: 'pos' });
      await queryClient.invalidateQueries({ queryKey: ['client', client.name] });
      onClose();
    } catch (e) {
      setFailure(e instanceof ApiFailure ? e : null);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} size="lg" title={t('contacts.dialogTitle')} description={client.title}
      confirmLabel={pendingLabel(t('contacts.confirm'), sending)} cancelLabel={t('contacts.cancel')} confirmDisabled={sending} onConfirm={submit}>
      <Stack gap={16}>
        {failure && <Alert tone="neg" title={failure.body.message}>{failure.body.hint}{causeOf(failure) ? ` ${t('errors.cause', { cause: causeOf(failure)! })}` : ''}</Alert>}
        {contacts.map((c, i) => (
          <FormSection key={i} variant="card" title={t('contacts.contact', { n: i + 1 })}>
            <Grid columns="2">
              <Select label={t('contacts.role')} value={c.role} onChange={v => update(i, { role: (v ?? 'technical') as Contact['role'] })}
                options={ROLES.map(r => ({ value: r, label: t(`client.contacts.roles.${r}`) }))} />
              <TextField label={t('contacts.name')} value={c.name} onChange={(e: { target: { value: string } }) => update(i, { name: e.target.value })} error={fieldError(i, 'name')} />
              <TextField label={t('contacts.email')} type="email" value={c.email} onChange={(e: { target: { value: string } }) => update(i, { email: e.target.value })} error={fieldError(i, 'email')} />
              {c.role === 'restitution' && (
                <TextField label={t('contacts.mobile')} hint={t('contacts.mobileHint')} value={c.mobile ?? ''} onChange={(e: { target: { value: string } }) => update(i, { mobile: e.target.value })} error={fieldError(i, 'mobile')} />
              )}
            </Grid>
            {contacts.length > 1 && <Button variant="ghost" size="sm" icon="delete" onClick={() => setContacts(list => list.filter((_, j) => j !== i))}>{t('contacts.remove')}</Button>}
          </FormSection>
        ))}
        <Button variant="secondary" icon="add" onClick={() => setContacts(list => [...list, { role: 'technical', name: '', email: '' }])}>{t('contacts.add')}</Button>
      </Stack>
    </Dialog>
  );
}
